<script setup lang="ts">
// The unnumbered card under the panels: the definitions every panel above is
// read with, and the two things this reading deliberately cannot say. A report
// that hides its gaps makes the reader invent them, so the missing residual
// window and the ignored clauses are stated here rather than left to be
// inferred from an absent column.
import { computed } from "vue";

import type { MapReviewWasteView } from "@/type/api";

import ReportPanel from "../report/ReportPanel.vue";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

/** The dimensions a visit-level reading has no column for, grouped the way the
 * ledger's own filters name them. */
const ignoredLabel = computed(() => {
	const groups = new Set<string>();
	for (const clause of props.view.ignored) {
		if (clause.table === "species_count") groups.add("species and sex");
		else if (clause.table === "zone") groups.add("zone");
		else groups.add("trap");
	}
	return [...groups].join(", ");
});
</script>

<template>
	<section class="waste-method">
		<ReportPanel title="Method and limits">
			<ul class="method-list">
				<li>
					<strong>Visit — </strong>one Site on one local day, derived from the
					observations kept in the season.
				</li>
				<li>
					<strong>Treated — </strong>a kept treatment at any Feature of that
					Site on that day. A treatment on one Feature marks the whole Site's
					visit treated.
				</li>
				<li>
					<strong>Reading — </strong>found means a live inspection on the visit
					recorded production; none means an inspection ran and found no
					production; no inspection means neither.
				</li>
				<li>
					<strong>Not all no-intervention visits are waste — </strong>a visit
					that checks a clean site buys information. The failure case is narrow,
					and it is the vermillion sliver: production was recorded and nothing
					was applied.
				</li>
				<li>
					<strong>What cannot be measured here — </strong>whether a treatment
					failed inside its promised window. No product and no intervention
					carries a residual duration, so
					<code>treatment.residual_expires</code> is null for every row. The
					return window is absent from this report rather than invented.
				</li>
				<li>
					<strong>Site-day key — </strong>a treatment on one Feature marks the
					whole Site's visit treated, so a Site-level waste reading is
					conservative: it can call a partially treated visit treated, never the
					reverse.
				</li>
				<li v-if="ignoredLabel">
					<strong>Ignored clauses — </strong>{{ ignoredLabel }} clauses do not
					narrow a visit-level waste reading, so they are reported as ignored
					rather than pretended. The panels count every visit in the current
					selection.
				</li>
			</ul>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-method {
	grid-column: 1 / -1;
	min-width: 0;
}

/* A compact list, not prose: each row is one rule the reader can check a panel
   against. The code span is the one database column the gap is stated in, so a
   reader who goes to the table knows what to look for. */
.method-list {
	margin: 0;
	padding-left: 18px;
	font-size: 0.66rem;
	line-height: 1.55;
	color: #495057;
	overflow-wrap: anywhere;
}

.method-list li {
	margin: 0 0 5px;
}

.method-list li:last-child {
	margin-bottom: 0;
}

.method-list strong {
	color: #212529;
	font-weight: 600;
}

.method-list code {
	padding: 0 3px;
	border-radius: 2px;
	background: #f1f3f5;
	color: #495057;
	font-size: 0.96em;
}
</style>
